import { useState, useEffect} from 'react';
import { useTheme } from '@mui/material/styles';
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import 'highcharts/modules/accessibility';
import { useErrorBoundary } from 'react-error-boundary';

import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Grid2 as Grid, Card, CardContent, Stack, FormControl, Select, MenuItem, useMediaQuery, InputLabel} from '@mui/material';

enum OfficType{
  Senate = 1 ,
  House = 2,
  Mayoral = 3,
  Council = 4,
  County = 5
}

interface dataLabelObject {
  y: number,
  office: string,
}

//HighchartsAccessibility(Highcharts); // Initialize the accessibility module

const MonthlyChart = (): JSX.Element => {

    const theme = useTheme();
    const { showBoundary } = useErrorBoundary();  
    const isMd = useMediaQuery(theme.breakpoints.up('md'));
    const isSM = useMediaQuery(theme.breakpoints.up('sm'));

    const [chartType, setChartType] = useState<string>('C-TOTAL');
    const [officeType, setOfficeType] = useState<number>(0);
    const [officeDescription, setOfficeDescription] = useState('');
    const [chartNames, setChartNames] = useState<string[]>();
    const [chartValues, setChartValues] = useState<dataLabelObject[]>();
    const [chartDate, setChartDate] = useState(dayjs(new Date().setDate(0)));
    const [chartTitle, setChartTitle] = useState<string>('');
    const [chartSubtitle, setChartSubtitle] = useState<string>('');
    const [chartTypeLabel, setChartTypeLabel] = useState<string>('Total Contributions');

    const [noActivity, setNoActivity] = useState<boolean>(true);
   
    const MonthlyChartOptions = {
      chart: {
          type: 'bar',
          height: 550,
          width: isSM? 850 : 400,
      },
      title: {
          text: chartTitle
      },
      accessibility: {
        enabled: true,
        description: 'A chart displaying Total Raised or Total Spent: Top 10 Candidates.',
        keyboardNavigation: {
          enabled: true
        }
      },
      subtitle: {
          text: chartSubtitle,
          style: {
              color: 'blue',
              fontWeight: '550'
          }
      },
      tooltip: {
          valueDecimals: 2,
          valuePrefix: '$'
      },
      xAxis: {
          categories: chartNames,
          title: {
              text: null
          },
          labels: {
              style: {
                  fontSize: '14px',
                  color: 'black',
                  fontWeight: '550'
              },
          },
          min: 0,
          lineWidth: 0,
          gridLineWidth: 0,
          lineColor: 'transparent',
          minorTickLength: 0,
          tickLength: 0
      },
      yAxis: {
          min: 0,
          title: {
              text: chartTypeLabel,
              align: 'high'
          },
          labels: {
              overflow: 'justify'
          },
          lineWidth: 0,
          minorGridLineWidth: 0,
          lineColor: 'transparent'
      },
      plotOptions: {
          bar: {
              dataLabels: {
                  enabled: true,
                  style: {
                      fontWeight: 'bold'
                  },
                  format: isMd?'${point.y:,.2f}' + ' <i>' + '{point.office}' + '</i>' : '${point.y:,.2f}'
              },
              cursor: 'pointer',
              colorByPoint: true,
              point: {
                  events: {
                      //click: updateGridData
                  }
              }
          }
      },
      legend: {
          enabled: false
      },
      credits: {
          enabled: false
      },
      exporting: {
          enabled: false
      },
      series: [
          {
              name: 'Contributions',
              data: chartValues
          }
      ]
    };

    useEffect(() => {
      getMonthlyChartData();
    }, [chartType, officeType, chartDate])


    const getMonthlyChartData = async () => {

      let chartStartDate = dayjs(new Date(chartDate.year(), chartDate.month(), 1)).format('MM/DD/YYYY');
      let chartEndDate = dayjs(new Date(chartDate.year(), chartDate.month()+1, 0)).format('MM/DD/YYYY');

      setChartNames([]);
      setChartValues([]);

      let params = [
        `chartType=${chartType}`,
        `startDate=${chartStartDate}`,
        `endDate=${chartEndDate}`,
        `officeTypeId=${officeType}`
      ].join('&');

      try{
        await fetch(`${process.env.REACT_APP_API_BASE_URL}/chartData/monthly?${params}`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get the monthly fundraising data.'));
          }
          return result.json();
        })
        .then(result => {
          let data = result;
          let filers: string[] = [];
          let values: dataLabelObject[] = [];

          data.forEach((item: any) => {
            let object: dataLabelObject;
            filers.push(item.filerName + ' (' + item.partyAffiliation + ')');
            object = {y: item.total, office: simplifyOfficeSought(item.officeSoughtDescription)};
            values.push(object);
          });

          setChartNames(filers);
          setChartValues(values);
          setNoActivity(false);
         
          let description = officeType > 0 ? `(${officeDescription})` : '';

          let title = chartType === 'C-TOTAL' ? `Total Raised: Top 10 Candidates ${description}` : `Total Spent: Top 10 Candidates ${description}`;
          let label = chartType === 'C-TOTAL' ? `Total Contributions` : `Total Expenditures`;
          setChartTitle(title);
          setChartTypeLabel(label);
          let subtitle = chartType === 'C-TOTAL' ? `Total contributions between ${chartStartDate} and ${chartEndDate}` : `Total expenditures between ${chartStartDate} and ${chartEndDate}`;
          setChartSubtitle(subtitle);

        });
      }catch(error){
          console.error('Uncaught Error:', error);
      }
    }

    const simplifyOfficeSought = (office: string) => {

      if (office.indexOf("Statewide") >= 0) {
          return office.replace("Statewide, ", "");
      }
      if (office.indexOf("Lieutenant") >= 0) {
          return office.replace("Lieutenant", "Lt.");
      }
      if (office.indexOf("Senate") >= 0) {
          return office.replace("Senate, ", "") + " (S)";
      }
      if (office.indexOf("House") >= 0) {
          return office.replace("House, ", "") + " (H)";
      }
      return office;
    }

    const updateChartType = (event: any) => {
      setChartType(event.target.value);
    }

    const updateOfficeType = (event: any) => {
      setOfficeType(event.target.value);
      switch (event.target.value) {
        case OfficType.Senate:
          setOfficeDescription('Senate');
          break;
        case OfficType.House:
            setOfficeDescription('House');
            break;
        case OfficType.Mayoral:
            setOfficeDescription('Mayoral');
            break;
        case OfficType.Council:
            setOfficeDescription('City Council');
            break;
        case OfficType.County:
            setOfficeDescription('County');
            break;
      }
    }


    return (
        <Grid size={12} data-aos={'fade-up'}>
          {
            !noActivity &&
            <Card variant="outlined" sx={{height: '100%', width: '100%'}}>                                              
            <CardContent>
                <Grid container 
                  spacing={1}
                  marginBottom={2}
                >
                 <Grid display={'flex'} justifyContent={'center'} size={12} marginBottom={2}>
                  <Stack direction="row" spacing={2} alignItems={'center'} useFlexGap flexWrap="wrap">
                    <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                      <InputLabel>Office Type</InputLabel>
                      <Select 
                        sx={{fontSize: 'smaller'}} 
                        label="Office Type"
                        value={officeType}
                        onChange={updateOfficeType}
                        >
                        <MenuItem value={0}>All Offices</MenuItem>
                        <MenuItem value={1}>Senate</MenuItem>
                        <MenuItem value={2}>House</MenuItem>
                        <MenuItem value={3}>Mayoral</MenuItem>
                        <MenuItem value={4}>City Council</MenuItem>
                        <MenuItem value={5}>County</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl sx={{ m: 1, minWidth: 120}} size="small">
                        <Select 
                          value={chartType}
                          sx={{fontSize: "smaller", padding: 0.2}}
                          onChange={updateChartType}
                        >
                          <MenuItem value={"C-TOTAL"}>Total Raised</MenuItem>
                          <MenuItem value={"E-TOTAL"}>Total Spent</MenuItem>
                        </Select>
                    </FormControl>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        sx={{ fontSize: 'smaller' }} 
                        slotProps={{ textField: { size: 'small' } }}
                        value={chartDate}
                        views={['year', 'month']}
                        label="Year and Month"
                        minDate={dayjs('2020-01-01')}
                        maxDate={chartDate}
                        onChange={(newValue) => {
                          if (newValue) {
                            setChartDate(newValue);
                          }
                        }}
                    />
                    </LocalizationProvider>
                  </Stack>
                 </Grid>
                 <Grid size={12} display={'flex'} justifyContent={'center'}>
                 <HighchartsReact
                    highcharts={Highcharts}
                    options={MonthlyChartOptions}
                  />
                  {
                    /* material ui chart
                    
                    <BarChart
                      series={[
                        { data: chartValues},
                      ]}
                      yAxis={[
                      {
                          data: chartNames,
                          scaleType: 'band',
                          colorMap: {
                              type: "ordinal",
                              values: chartNames,
                              colors: chartColors
                          },
                          categoryGapRatio: 0.35
                      } as AxisConfig<'band', any, ChartsYAxisProps>
                      ]}
                      layout="horizontal"
                      width={650}
                      margin={{ top: 15, bottom: 30, left:150, right: 10 }}
                      barLabel={(item) => {
                        const formattedAmount = new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                        }).format(item.value ?? 0);
                          return `${formattedAmount}`;
                        }}
                      sx={{ 
                          '.MuiChartsAxis-tickLabel': { 
                              fontWeight: '550 !important',
                          },
                          }}              
                    />
                    
                    
                    */
                  }
                  
                 </Grid>
                </Grid>  
            </CardContent>                                               
            </Card>
          }
        </Grid>
    );
  };

export default MonthlyChart;